<script setup>
import IndexNavbar from "@/components/Navbars/IndexNavbar.vue";
import FooterComponent from "@/components/Footers/Footer.vue";
import {ref, onMounted} from "vue";
import {ElLoading, ElMessage, ElTable, ElTableColumn} from "element-plus";
import * as echarts from "echarts";

// Neo4j数据库连接配置
const neo4j = require('neo4j-driver');
const driver = neo4j.driver(
    'bolt://bioinfo-sysu.com:7687',
    neo4j.auth.basic('neo4j', 'neo4j@bioinfo')
);

const entitytype = ref('herb')
const entityname = ref('Aspergillus terreus')
const graphChart = ref(null); // ECharts实例引用
let chartInstance = null; // ECharts实例
let currentData = null; // 存储当前图表数据
const combinedTableData = ref([]); // 合并后的表格数据
const showTable = ref(false); // 控制表格显示

// 初始化图表
onMounted(() => {
  chartInstance = echarts.init(document.getElementById('graphChart'));
  // 添加图例点击事件处理
  chartInstance.on('legendselectchanged', handleLegendClick);
});

// 颜色映射表 - 为每种类型指定颜色
const COLOR_MAP = {
  'SPECIES': '#91cc75',
  'CELL_LINE': '#fc8252',
  'DNA': '#5470c6',
  'GENE': '#73c0de',
  'DRUG': '#9a60b4',
  'DISEASE': '#ee6666',
  'CELL_TYPE': '#fac858',
  'RNA': '#3ba272',
  'HERB': '#2ecc71',    // ✅ 新增颜色（绿色调代表植物）
  'default': '#ff9b9b'
};
const graphBackground = ref('white'); // 初始为白色

// 图标符号映射表 - 为每种类型指定不同图标
const SYMBOL_MAP = {
  'SPECIES': 'circle',
  'CELL_LINE': 'rect',
  'DNA': 'diamond',
  'GENE': 'pin',
  'DRUG': 'roundRect',
  'DISEASE': 'triangle',
  'CELL_TYPE': 'arrow',
  'RNA': 'star',
  'HERB': 'hexagon',   // ✅ 新增图标（可换成 'circle'、'diamond' 等）
  'default': 'circle'
};


// 处理Neo4j结果
const processNeo4jResult = (records) => {
  const nodes = [];
  const links = [];
  const nodeMap = new Map(); // key: type+name -> 节点id
  const combinedData = []; // 合并后的表格数据
  const uniqueRows = new Set(); // 用于去重表格行

  let nodeCounter = 0; // 用于生成唯一ID

  records.forEach(record => {
    const startNode = record.get('n');
    const relationship = record.get('r');
    const endNode = record.get('m');

    const startType = startNode.labels[0] || 'default';
    const endType = endNode.labels[0] || 'default';

    // 创建唯一key按 type + name 合并节点
    const startKey = `${startType}:${startNode.properties.name}`;
    const endKey = `${endType}:${endNode.properties.name}`;

    // 起点节点
    let startNodeId;
    if (!nodeMap.has(startKey)) {
      startNodeId = `node_${nodeCounter++}`;
      nodes.push({
        id: startNodeId,
        name: startNode.properties.name,
        type: startType,
        symbolSize: 30,
        symbol: SYMBOL_MAP[startType] || SYMBOL_MAP.default,
        itemStyle: { color: COLOR_MAP[startType] || COLOR_MAP.default }
      });
      nodeMap.set(startKey, startNodeId);
    } else {
      startNodeId = nodeMap.get(startKey);
    }

    // 终点节点
    let endNodeId;
    if (!nodeMap.has(endKey)) {
      endNodeId = `node_${nodeCounter++}`;
      nodes.push({
        id: endNodeId,
        name: endNode.properties.name,
        type: endType,
        symbolSize: 30,
        symbol: SYMBOL_MAP[endType] || SYMBOL_MAP.default,
        itemStyle: { color: COLOR_MAP[endType] || COLOR_MAP.default }
      });
      nodeMap.set(endKey, endNodeId);
    } else {
      endNodeId = nodeMap.get(endKey);
    }

    // 添加关系
    links.push({
      source: startNodeId,
      target: endNodeId,
      name: relationship.type,
      lineStyle: { width: 2, curveness: 0.2, color: '#90979c' }
    });

    // 表格数据
    const rowData = {
      id: endNode.properties.node_id,
      name: startNode.properties.name,
      relatedEntity: endNode.properties.name,
      relatedType: endType,
      relationshipType: relationship.type,
      pmid: relationship.properties.pmid || 'N/A',
      context: relationship.properties.context || 'N/A'
    };

    const rowKey = `${rowData.id}-${rowData.relatedEntity}-${rowData.relationshipType}-${rowData.pmid}`;
    if (!uniqueRows.has(rowKey)) {
      combinedData.push(rowData);
      uniqueRows.add(rowKey);
    }
  });

  return { nodes, links, combinedData };
};


// 修复图例点击事件处理 - 使用事件对象获取准确状态
const handleLegendClick = (params) => {
  if (!currentData || !params.selected) return;

  const selectedState = params.selected;

  // 只保留被选中的类型的节点
  const filteredNodes = currentData.nodes.filter(node => selectedState[node.type] !== false);

  // 保留两端都在filteredNodes中的边
  const nodeIds = new Set(filteredNodes.map(n => n.id));
  const filteredLinks = currentData.links.filter(
      link => nodeIds.has(link.source) && nodeIds.has(link.target)
  );

  // 更新ECharts图表
  chartInstance.setOption({
    series: [{
      data: filteredNodes,
      links: filteredLinks
    }],
    animationDurationUpdate: 800,
    animationEasingUpdate: 'quinticInOut'
  });
};


// 绘制网络图
const drawGraph = (data) => {
  if (!data.nodes.length) {
    ElMessage.warning('No data available for display');
    return;
  }

  chartInstance.clear();
  currentData = data; // 存储当前数据用于图例点击处理

  // 收集所有类型用于图例
  const typesSet = new Set();
  data.nodes.forEach(node => typesSet.add(node.type));
  const types = Array.from(typesSet);

  // 设置图例默认选中状态
  const selectedState = {};
  types.forEach(type => selectedState[type] = true);

  // 创建图例数据
  const legendData = types.map(type => ({
    name: type,
    icon: SYMBOL_MAP[type] || 'circle',
    itemStyle: {
      color: COLOR_MAP[type] || COLOR_MAP.default
    }
  }));

  const option = {
    tooltip: {
      formatter: function (params) {
        if (params.dataType === 'node') {
          return `<div style="text-align:left;">
              <b>${params.data.name}</b><br/>
              Type: ${params.data.type}<br/>
              ID: ${params.data.id}</div>`;
        }
        return '';
      }
    },
    legend: {
      data: legendData,
      selected: selectedState,
      textStyle: {
        fontWeight: 'bold',
        color: '#333'
      },
      formatter: function (name) {
        // 只显示类型名称，不显示形状文字
        return name;
      },
      tooltip: {
        show: true,
        formatter: function (params) {
          return `Click to toggle ${params.name} nodes`;
        }
      }
    },
    animationDuration: 1500,
    animationEasingUpdate: 'quinticInOut',
    series: [{
      type: 'graph',
      layout: 'force',
      draggable: true,
      roam: true,
      focusNodeAdjacency: true,
      force: {
        repulsion: 400,
        gravity: 0.1,
        edgeLength: 150,
        friction: 0.6
      },
      categories: types.map(type => ({name: type})),
      label: {
        show: true,
        position: 'bottom',
        fontSize: 12,
        fontWeight: 'bold',
        formatter: '{b}'
      },
      edgeLabel: {
        show: false // 不显示关系文字
      },
      emphasis: {
        scale: true,
        focus: 'adjacency',
        label: {
          show: true,
          fontSize: 14
        }
      },
      data: data.nodes,
      links: data.links,
      lineStyle: {
        opacity: 0.9,
        width: 2,
        curveness: 0
      }
    }]
  };

  chartInstance.setOption(option);
  chartInstance.resize(); // 确保正确渲染
};

// 查询数据
const fetchData = async () => {

  if (!entityname.value.trim()) {
    ElMessage.warning('Please enter an entity name');
    return;
  }
  isPicshow.value = false
  const loading = ElLoading.service({
    lock: true,
    text: 'Loading...',
    background: 'rgba(0, 0, 0, 0.7)'
  });

  try {
    const session = driver.session();
    const labelMapping = {
      'DNA': 'GENE',
      'disease': 'DISEASE',
      'species': 'SPECIES',
      'cell_type': 'CELL_TYPE',
      'cell_line': 'CELL_LINE',
      'gene': 'GENE',
      'drug': 'DRUG',
      'RNA': 'RNA',
      'herb': 'HERB'   // ✅ 新增：支持HERB节点类型
    };


    const label = labelMapping[entitytype.value];
    const query = `
        MATCH (n:${label}) WHERE toLower(n.name) CONTAINS toLower($entityname)
        MATCH (n)-[r]-(m)
        RETURN n, r, m
        LIMIT 100
      `;

    const result = await session.run(query, {entityname: entityname.value});
    const processedData = processNeo4jResult(result.records);
    drawGraph(processedData);
    // 更新合并后的表格数据
    combinedTableData.value = processedData.combinedData;
    showTable.value = true; // 显示表格
    graphBackground.value = '#f0f0f0';
  } catch (error) {
    console.error('Neo4j error:', error);
    ElMessage.error(`Query failed: ${error.message}`);
    showTable.value = false;
  } finally {
    loading.close();
    isPicshow.value = true
  }
};

// 取消操作
const cancel = () => {
  entityname.value = '';
  isPicshow.value = false
  showTable.value = false; // 隐藏表格
  if (chartInstance) {
    chartInstance.clear();
  }
  currentData = null;
  combinedTableData.value = []; // 清空表格数据
  graphBackground.value = 'white';
};

const isPicshow = ref(false)
</script>
<template>
  <div>
    <index-navbar/>
    <section class="relative w-full h-full py-40 min-h-screen">
      <div class="container mx-auto items-center flex flex-wrap">
        <div class="col-12">
          <div class="text-center mb-8">
                <span class="text-blueGray-500 text-lg font-bold" style="font-size:35px">
                  Knowledge Graph
                 </span>
          </div>
        </div>
        <div class="col-12">
          <div class="py-10 border-t border-blueGray-200 text-center">
            <div class="flex flex-wrap justify-center">
              <div class="w-full lg:w-9/12 px-4">
                <p class="text-lg leading-relaxed" style="color: #ccc;">

                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="col-12 d-flex justify-content-center flex-column">
          <div class="col-12">
            <table class="table table-md align-middle table-borderless"
                   style="table-layout:fixed">
              <tbody>
              <tr>
                <td style="word-break:break-all; word-wrap:break-word; white-space:inherit;width: 30%;text-align: right;">
                  Entity
                </td>
                <td style="word-break:break-all; word-wrap:break-word; white-space:inherit;width: 20%;text-align: center">
                  <select class="form-select form-select-lg mx-1" aria-label=".form-select-lg example"
                          v-model="entitytype"
                          style="color: #ccc;">
                    <!--                    <option value="DNA" style="color: black;">DNA</option>-->
                    <!--                    <option value="RNA" style="color: black;">RNA</option>-->
                    <!--                    <option value="disease" style="color: black;">disease</option>-->
                    <!--                    <option value="species" style="color: black;">species</option>-->
                    <!--                    <option value="cell_type" style="color: black;">cell type</option>-->
                    <!--                    <option value="cell_line" style="color: black;">cell line</option>-->
                    <option value="gene" style="color: black;">gene</option>
                    <option value="herb" style="color: black;">herb</option>
                  </select>
                </td>
                <td style="word-break:break-all; word-wrap:break-word; white-space:inherit;width: 30%;text-align: center">
                  <input class="form-control form-control-lg mx-4" type="text" aria-label=".form-control-lg example"
                         style="width: 90%" v-model="entityname">
                </td>
                <td style="word-break:break-all; word-wrap:break-word; white-space:inherit;width: 20%;text-align: left;">
                    <span style="color: #ccc;">
                      e.g., "B2M"
                    </span>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
          <div class="align-self-center">
            <button
                class="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-base px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 mx-2"
                type="button"
                @click="fetchData">
              Load Network
            </button>
            <button
                class="bg-red-500 text-white active:bg-red-600 font-bold uppercase text-base px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 mx-2"
                type="button"
                @click="cancel">
              Cancel
            </button>
          </div>
          <div id="tablePagination" class="row justify-content-center">
            <div class="col-12">
              <div id="graphChart" :style="{ backgroundColor: graphBackground }" style="width: 100%; height: 600px;"></div>
            </div>
          </div>

          <div v-if="showTable" class="mt-8 w-full">
            <div class="text-center mb-4">
              <h3 class="text-xl font-bold">Entity Relationship Information</h3>
            </div>
            <div class="bg-white rounded-lg shadow overflow-hidden">
              <el-table :data="combinedTableData" stripe style="width: 100%" empty-text="No data available">
                <!-- ID列 - 显示相关实体的ID -->
                <el-table-column prop="id" label="ID" width="220">
                  <template #default="scope">
                    <div>
                <span v-if="scope.row.id.startsWith('mesh:')">
                  <a :href="`https://meshb.nlm.nih.gov/record/ui?ui=${scope.row.id.split(':')[1]}`"
                     target="_blank"
                     style="color: #409EFF; text-decoration: none;">
                    {{ scope.row.id }}
                  </a>
                </span>
                      <span v-else-if="scope.row.id.startsWith('NCBIGene:')">
                  <a :href="`https://www.ncbi.nlm.nih.gov/gene/${scope.row.id.split(':')[1]}`"
                     target="_blank"
                     style="color: #409EFF; text-decoration: none;">
                    {{ scope.row.id }}
                  </a>
                </span>
                      <span v-else-if="scope.row.id.startsWith('NCBITaxon:')">
                  <a :href="`https://www.ncbi.nlm.nih.gov/Taxonomy/Browser/wwwtax.cgi?id=${scope.row.id.split(':')[1]}`"
                     target="_blank"
                     style="color: #409EFF; text-decoration: none;">
                    {{ scope.row.id }}
                  </a>
                </span>
                      <span v-else>NA</span>
                    </div>
                  </template>
                </el-table-column>


                <!-- 相关实体名称 -->
                <el-table-column prop="relatedEntity" label="Related Entity" width="180"/>

                <!-- 相关实体类型 -->
                <el-table-column prop="relatedType" label="Related Type" width="120"/>


                <!-- PMID列 -->
                <el-table-column prop="pmid" label="PMID" width="100">
                  <template #default="scope">
                    <a v-if="scope.row.pmid !== 'N/A'" :href="`https://pubmed.ncbi.nlm.nih.gov/${scope.row.pmid}`"
                       target="_blank" style="color: #409EFF; text-decoration: none;">
                      {{ scope.row.pmid }}
                    </a>
                    <span v-else>N/A</span>
                  </template>
                </el-table-column>

                <!-- 上下文 -->
                <el-table-column prop="context" label="Context">
                  <template #default="scope">
                    <div style="white-space: normal; word-break: break-all;">
                      {{ scope.row.context }}
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
      </div>
    </section>
    <footer-component/>
  </div>
</template>
<style scoped>
/* 添加自定义样式 */
#graphChart {
  border-radius: 8px;
  background-color: white; /* 默认白色背景 */
  margin-top: 20px;
}

/* 表格容器样式 */
.node-table-container {
  margin-top: 40px;
  padding: 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}
</style>